import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Select, Modal, Form, Radio, InputNumber, Input, message } from "antd";
import SearchBar from "../../components/searchBar";
import DateRangeFilter from "../../components/dateRangeFilter";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import { useGetAllTrainersIncentiveHistoryQuery, useGetTrainersQuery, usePayTrainerIncentiveMutation } from "../../services/trainer";
import { getAddonIncentiveColumns, AddonIncentiveRecord } from "./columns";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface DateRange {
  start: Date;
  end:   Date;
}

/** Format a Date as YYYY-MM-DD using local time (avoids the UTC off-by-one from toISOString). */
const toLocalDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const statusOptions = [
  { value: 'all',         label: 'All Status'    },
  { value: 'unpaid',      label: 'Unpaid'        },
  { value: 'partialpaid', label: 'Partial Paid'  },
  { value: 'fullpaid',    label: 'Full Paid'     },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const AddonIncentive = () => {
  const selectedBranch = useSelector((state: any) => state.branch.selectedBranch);
  const branchId = typeof selectedBranch === 'object' ? (selectedBranch?._id || selectedBranch?.id) : selectedBranch;

  const [page,         setPage]         = useState(1);
  const [limit,        setLimit]        = useState(10);
  const [searchText,   setSearchText]   = useState('');
  const [trainerId,    setTrainerId]    = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange,    setDateRange]    = useState<DateRange | null>(null);

  useEffect(() => { setPage(1); }, [branchId]);

  const { data, isFetching } = useGetAllTrainersIncentiveHistoryQuery({
    page,
    limit,
    search:    searchText || undefined,
    trainerId: trainerId !== 'all' ? trainerId : undefined,
    branchId:  branchId || undefined,
    status:    statusFilter !== 'all' ? statusFilter : undefined,
    fromDate:  dateRange?.start ? toLocalDateStr(dateRange.start) : undefined,
    toDate:    dateRange?.end   ? toLocalDateStr(dateRange.end)   : undefined,
  });

  const { data: trainersData } = useGetTrainersQuery({ limit: 1000, status: 'ACTIVE' });

  const trainerOptions = useMemo(() => {
    const td = trainersData as any;
    const list = td?.data ?? td?.trainers;
    const opts = Array.isArray(list)
      ? list
          .filter((t: any) => String(t?.status ?? t?.user?.status ?? '').toUpperCase() === 'ACTIVE')
          .map((t: any) => ({ label: t?.user?.name ?? t?.name, value: t?._id ?? t?.id }))
          .filter((o: any) => o.label && o.value)
      : [];
    return [{ label: 'All Trainers', value: 'all' }, ...opts];
  }, [trainersData]);

  const records: AddonIncentiveRecord[] = useMemo(() => {
    const rows: any[] = (data as any)?.data ?? [];
    return rows.map((x: any) => {
      const finalTotal = Number(x.finalTotal ?? x.totalAmount ?? 0);
      const paidAmount = Number(x.paidAmount ?? 0);
      const payments: any[] = Array.isArray(x.payments) ? x.payments : [];
      const paidByNames = [...new Set(
        payments.map(p => (typeof p.paidBy === 'object' ? p.paidBy?.name : p.paidBy)).filter(Boolean),
      )] as string[];
      const lastPaidAt = payments.map(p => p.paidAt).filter(Boolean).sort().pop();
      const rewardAmount = Array.isArray(x.additionalFields)
        ? x.additionalFields.reduce((sum: number, f: any) => sum + (Number(f.amount) || 0), 0)
        : 0;
      return {
        id:               x._id ?? x.id,
        trainerId:        typeof x.trainerId === 'object' ? (x.trainerId?._id ?? x.trainerId?.id) : x.trainerId,
        trainerName:      x.trainerName ?? x.trainerId?.user?.name,
        trainerPhone:     x.trainerPhoneNumber ?? x.trainerId?.user?.phoneNumber,
        trainerBranches:  x.trainerBranches ?? x.trainerId?.user?.branchIds ?? [],
        fromDate:         x.fromDate,
        toDate:           x.toDate,
        noOfSessions:     Number(x.noOfSessions ?? x.totalSessions ?? 0),
        totalClients:     Number(x.totalClients ?? 0),
        revenueGenerated: Number(x.revenueGenerated ?? 0),
        slabPercentage:   Number(x.slabPercentage ?? 0),
        incentiveAmount:  Number(x.incentiveAmount ?? 0),
        tdsAmount:        Number(x.tdsAmount ?? 0),
        gstAmount:        Number(x.gstAmount ?? 0),
        deduction:        Number(x.deduction ?? 0),
        rewardAmount,
        rewardFields:     Array.isArray(x.additionalFields) ? x.additionalFields : [],
        finalTotal,
        paidAmount,
        dueAmount:        Number(x.dueAmount ?? (finalTotal - paidAmount)),
        status:           x.status,
        generatedByName:  x.generatedBy?.name,
        paidByNames,
        lastPaidAt,
        generatedAt:      x.createdAt,
      };
    });
  }, [data]);

  const total = (data as any)?.total ?? records.length;

  // ── Pay modal state (same flow as the per-trainer Incentive History page) ──
  const [payTrainerIncentive, { isLoading: paying }] = usePayTrainerIncentiveMutation();
  const [payEntry,        setPayEntry]        = useState<AddonIncentiveRecord | null>(null);
  const [paymentMode,     setPaymentMode]     = useState<'partial' | 'full'>('full');
  const [paymentAmount,   setPaymentAmount]   = useState<number | null>(0);
  const [paymentNote,     setPaymentNote]     = useState('');
  const [deduction,       setDeduction]       = useState<number | null>(0);
  const [deductionRemark, setDeductionRemark] = useState('');

  const openPayModal = (entry: AddonIncentiveRecord) => {
    setPayEntry(entry);
    setPaymentMode('full');
    setPaymentAmount(Math.max(0, entry.dueAmount ?? 0));
    setPaymentNote('');
    setDeduction(0);
    setDeductionRemark('');
  };

  const closePayModal = () => setPayEntry(null);

  const handlePaySubmit = async () => {
    if (!payEntry || !payEntry.trainerId || !payEntry.id) return;
    const due = Math.max(0, payEntry.dueAmount ?? 0);
    const amount = paymentMode === 'full' ? due : Math.min(Number(paymentAmount) || 0, due);
    if (amount <= 0) { message.error('Enter a valid amount'); return; }

    try {
      await payTrainerIncentive({
        trainerId:   payEntry.trainerId,
        incentiveId: payEntry.id,
        paymentType: paymentMode,
        amount,
        note: paymentNote,
        ...(deduction ? { deduction, deductionRemark } : {}),
      }).unwrap();
      closePayModal();
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  const columns = getAddonIncentiveColumns(openPayModal);

  return (
    <>
    <div className="addon-incentive-page">
      <div className="header-section">
        <SearchBar
          value={searchText}
          onChange={(val: string) => { setSearchText(val); setPage(1); }}
          placeholder="Search by trainer name or number..."
        />
        <Select
          showSearch
          value={trainerId}
          onChange={(val: string) => { setTrainerId(val); setPage(1); }}
          className="addon-incentive-filter"
          style={{ width: 180, height: 41 }}
          options={trainerOptions}
          optionFilterProp="label"
        />
        <Select
          value={statusFilter}
          onChange={(val: string) => { setStatusFilter(val); setPage(1); }}
          className="addon-incentive-filter"
          style={{ width: 150, height: 41 }}
          options={statusOptions}
        />
        <DateRangeFilter
          value={dateRange}
          onChange={(v: DateRange | null) => { setDateRange(v); setPage(1); }}
        />
      </div>

      <div className="addon-incentive-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isFetching}
          pagination={false}
          rowKey={(record: AddonIncentiveRecord) => record.id as string}
          scroll={{ x: 1700 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={total}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>

    {/* ── Pay modal — same flow as the per-trainer Incentive History page ── */}
    <Modal
      title="Record Payment"
      open={!!payEntry}
      onCancel={closePayModal}
      onOk={handlePaySubmit}
      okText="Pay"
      confirmLoading={paying}
      destroyOnHidden
    >
      {payEntry && (
        <Form layout="vertical">
          <Form.Item label="Payment Mode">
            <Radio.Group
              value={paymentMode}
              onChange={(e) => {
                const mode = e.target.value;
                setPaymentMode(mode);
                setPaymentAmount(mode === 'full' ? Math.max(0, payEntry.dueAmount ?? 0) : null);
              }}
            >
              <Radio.Button value="full">Fully Paid</Radio.Button>
              <Radio.Button value="partial">Partial</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Amount">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={Math.max(0, payEntry.dueAmount ?? 0)}
              value={paymentAmount}
              placeholder={paymentMode === 'partial' ? 'Enter amount' : undefined}
              disabled={paymentMode === 'full'}
              onChange={(v) => setPaymentAmount(v === null ? null : Number(v) || 0)}
            />
          </Form.Item>
          <Form.Item label="Note">
            <Input.TextArea
              rows={2}
              placeholder="e.g. 1st installment"
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Deduction (optional)">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              placeholder="e.g. advance already paid"
              value={deduction}
              onChange={(v) => setDeduction(v === null ? null : Number(v) || 0)}
            />
          </Form.Item>
          {!!deduction && (
            <Form.Item label="Deduction Remark">
              <Input.TextArea
                rows={2}
                placeholder="e.g. Advance already diya tha, wahi adjust kiya"
                value={deductionRemark}
                onChange={(e) => setDeductionRemark(e.target.value)}
              />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
    </>
  );
};

export default AddonIncentive;
